/**
 * Project — a named bucket users group their Knods into.
 *
 * Introduced by migration 095_projects.sql as Phase 1 of the Projects
 * rollout. See `docs/MCP_REVIEW_AND_ROADMAP.md` for the broader plan.
 *
 * The implicit "Inbox" (where flows with `projectId === null` live) is
 * NOT a row in this table — the UI synthesizes it. That keeps the
 * migration zero-write and mirrors how `org_id IS NULL` means "personal"
 * for the orgs feature.
 */

export interface Project {
  id: string;
  userId: string;
  orgId?: string | null;
  name: string;
  description?: string | null;
  color?: string | null;
  emoji?: string | null;
  sortOrder: number;
  archived: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * Shape used when creating a project — server fills in defaults.
 */
export interface ProjectCreateInput {
  name: string;
  description?: string | null;
  color?: string | null;
  emoji?: string | null;
  orgId?: string | null;
}

/**
 * Shape used when updating a project — every field is optional.
 */
export interface ProjectUpdateInput {
  name?: string;
  description?: string | null;
  color?: string | null;
  emoji?: string | null;
  sortOrder?: number;
  archived?: boolean;
}

/**
 * Sentinel for the implicit "Inbox" group when filtering by project.
 * Use this value where a real project id would go to mean "show flows
 * with projectId === null". The storage service translates it to a
 * `.is('project_id', null)` filter.
 */
export const INBOX_PROJECT_ID = '__inbox__' as const;
export type InboxProjectId = typeof INBOX_PROJECT_ID;

/**
 * The selector used by callers that want either a real project or Inbox.
 */
export type ProjectFilter = string | InboxProjectId | null | undefined;
